import React, { useState } from "react";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, Loader2, Download, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { inventoryApi } from "./inventoryApi";

const TEMPLATE_HEADERS = ["name", "part_number", "category", "subcategory", "manufacturer", "brand", "description", "oem_part_number", "quantity_on_hand", "unit_cost", "reorder_point", "value_type", "notes"];

const parseCsv = (text) => {
  const parseLine = (line) => {
    const out = []; let cur = ""; let q = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (q) {
        if (ch === '"') { if (line[i + 1] === '"') { cur += '"'; i++; } else q = false; }
        else cur += ch;
      } else if (ch === '"') q = true;
      else if (ch === ",") { out.push(cur); cur = ""; }
      else cur += ch;
    }
    out.push(cur);
    return out;
  };
  const lines = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n").filter((l) => l.trim().length);
  if (!lines.length) return [];
  const header = parseLine(lines[0]).map((h) => h.trim().toLowerCase().replace(/\s+/g, "_"));
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const cols = parseLine(lines[i]);
    const obj = {};
    header.forEach((h, idx) => { obj[h] = (cols[idx] ?? "").trim(); });
    rows.push(obj);
  }
  return rows;
};

const downloadTemplate = () => {
  const sample = `${TEMPLATE_HEADERS.join(",")}\n"Air Filter","AF-1001","Filters","","Fleetguard","","Primary air filter","","10","24.50","3","actual","Top shelf"`;
  const blob = new Blob([sample], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = "parts_import_template.csv"; a.click();
  URL.revokeObjectURL(url);
};

export const BulkImportModal = ({ open, onClose, currentUser, onImported }) => {
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const userName = currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : "Unknown";

  const reset = () => { setRows([]); setFileName(""); setResult(null); };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setResult(null);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed = parseCsv(ev.target.result);
        if (!parsed.length) { toast.error("No rows found in file"); return; }
        if (!("part_number" in parsed[0]) || !("name" in parsed[0])) {
          toast.error("CSV must include 'name' and 'part_number' columns");
          return;
        }
        setRows(parsed);
        setFileName(file.name);
      } catch {
        toast.error("Could not read CSV file");
      }
    };
    reader.readAsText(file);
  };

  const submit = async () => {
    if (!rows.length) return;
    setBusy(true);
    try {
      const res = await inventoryApi.bulkImportParts({ rows, created_by: userName });
      setResult(res);
      if (res.created > 0) onImported();
      toast.success(`Imported ${res.created} part(s)${res.skipped ? `, ${res.skipped} skipped` : ""}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { reset(); onClose(); } }}>
      <DialogContent className="max-w-lg" data-testid="bulk-import-modal">
        <DialogTitle className="sr-only">Import Parts</DialogTitle>
        <DialogDescription className="sr-only">Bulk import parts from a CSV file</DialogDescription>
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-lg font-semibold">Import Parts (CSV)</h3>
          <button onClick={() => { reset(); onClose(); }} className="p-1.5 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <p className="text-sm text-slate-500 mb-3">Upload a CSV to add many parts at once. Duplicate part numbers are skipped.</p>

        <button onClick={downloadTemplate} className="text-sm text-blue-600 hover:text-blue-700 inline-flex items-center gap-1 mb-3" data-testid="download-template">
          <Download className="w-4 h-4" /> Download CSV template
        </button>

        {!result && (
          <>
            <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-200 rounded-xl py-8 cursor-pointer hover:border-blue-300 hover:bg-slate-50 transition-colors">
              <FileSpreadsheet className="w-8 h-8 text-slate-400" />
              <span className="text-sm text-slate-600">{fileName || "Click to choose a CSV file"}</span>
              {rows.length > 0 && <span className="text-xs text-green-600 font-medium">{rows.length} row(s) ready to import</span>}
              <input type="file" accept=".csv,text/csv" className="hidden" onChange={handleFile} data-testid="import-file-input" />
            </label>

            <div className="flex gap-2 mt-4">
              <Button variant="outline" className="flex-1" onClick={() => { reset(); onClose(); }}>Cancel</Button>
              <Button className="flex-1" onClick={submit} disabled={!rows.length || busy} data-testid="import-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mr-1" /> Import {rows.length || ""}</>}
              </Button>
            </div>
          </>
        )}

        {result && (
          <div className="space-y-3" data-testid="import-result">
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="bg-green-50 rounded-lg p-3"><div className="text-2xl font-bold text-green-600">{result.created}</div><div className="text-xs text-slate-500">Created</div></div>
              <div className="bg-amber-50 rounded-lg p-3"><div className="text-2xl font-bold text-amber-600">{result.skipped}</div><div className="text-xs text-slate-500">Skipped (dup)</div></div>
              <div className="bg-red-50 rounded-lg p-3"><div className="text-2xl font-bold text-red-600">{result.error_count}</div><div className="text-xs text-slate-500">Errors</div></div>
            </div>
            {result.errors?.length > 0 && (
              <div className="max-h-32 overflow-y-auto text-xs text-red-600 bg-red-50 rounded-lg p-2">
                {result.errors.map((e, i) => <div key={i}>Row {e.row}: {e.error}</div>)}
              </div>
            )}
            <Button className="w-full" onClick={() => { reset(); onClose(); }}>Done</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
